import { PaymentsOutlined, VisibilityRounded } from "@mui/icons-material";
import { Box, CardActions, CardContent, Chip, IconButton, Tooltip, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { useQuery } from "react-query";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import Loading from "../../../../components/loading/loading";
import OrgSelector from "../../../../components/org_selector/org_selector";
import { ToolbarButton } from "../../../../components/toolbar_button/toolbar_button";
import { useFeedback } from "../../../../providers/feedback/feeedback";
import { queryClient } from "../../../../providers/query/query";
import useUser from "../../../../providers/user/user_provider";
import { getRedemptionRequests, updateRedemptionPayout } from "../../../../services/rewards/rewards";
import { useSelectedOrgStore } from "../../../../store/use-selected-org-store";
import { black, green, greenLight, greyEnabled, purple, red, redLight } from "../../../../theme/colors";
import {
  TableCell,
  TableEmptyState,
  TableFooterPagination,
  TableHeaderRow,
  TableRow,
} from "../../../users/components/table_toolkit/table_toolkit";
import { RedemptionRequestModel } from "../../model/redemption_request";
import FilterIcon from "../../../../assets/filter_icon.svg";
import FilterRedemptionRequests, {
  RedemptionRequestFilterValues,
} from "./filter_redemption_requests";
import UpdatePayoutDialog from "./update_payout_dialog";

const ROWS_PER_PAGE_OPTIONS = [8, 25, 50, 100];

const EMPTY_FILTERS: RedemptionRequestFilterValues = { status: null, payoutStatus: null };

function statusChipColors(status: string) {
  switch (status) {
    case "Completed":
      return { bg: "#E2F0D9", color: "#2E7D32" };
    case "In Progress":
      return { bg: "#E3F2FD", color: "#1976d2" };
    case "Rejected":
    case "Failed":
      return { bg: redLight, color: red };
    case "Pending":
    default:
      return { bg: "#FFF4CC", color: "#B26A00" };
  }
}

export default function RedemptionRequestsList() {
  const navigate = useNavigate();
  const { hasPermission } = useUser();
  const { showSnackbar } = useFeedback();
  const { selectedOrgId } = useSelectedOrgStore();

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(ROWS_PER_PAGE_OPTIONS[0]);
  const [filterDialogOpen, setFilterDialogOpen] = useState(false);
  const [filters, setFilters] = useState<RedemptionRequestFilterValues>(EMPTY_FILTERS);

  const [payoutTarget, setPayoutTarget] = useState<RedemptionRequestModel | null>(null);
  const [payoutSubmitting, setPayoutSubmitting] = useState(false);

  const activeFilterCount = [filters.status, filters.payoutStatus].filter(Boolean).length;

  useEffect(() => {
    setPage(1);
  }, [filters, selectedOrgId, pageSize]);

  const { data, isLoading, isError } = useQuery(
    ["redemptionRequestsListing", page, pageSize, filters, selectedOrgId],
    async () => {
      let response = await getRedemptionRequests({
        page,
        size: pageSize,
        status: filters.status,
        payoutStatus: filters.payoutStatus,
        orgId: selectedOrgId,
      });
      return response?.data?.data as { total: number; list: RedemptionRequestModel[] };
    }
  );

  const handleChangePage = (
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ) => {
    setPage(newPage + 1);
  };

  const count = data?.total || 0;
  const list = data?.list || [];
  const startRange = (page - 1) * pageSize + 1;
  const endRange = Math.min(page * pageSize, count);
  const customText = `Showing ${count ? startRange : 0} to ${endRange || 0} of ${count || 0} entries`;
  const showList = !isLoading && !isError && list.length > 0;
  const showEmpty = !isLoading && !isError && list.length === 0;

  function closePayout() {
    if (payoutSubmitting) return;
    setPayoutTarget(null);
  }

  function confirmPayout(payload: {
    payoutStatus: "Processing" | "Released" | "Failed";
    payoutReference?: string;
    payoutRemarks?: string;
  }) {
    if (!payoutTarget) return;
    setPayoutSubmitting(true);
    updateRedemptionPayout({ requestId: payoutTarget.id, ...payload })
      .then(() => {
        showSnackbar("Payout status updated");
        queryClient.invalidateQueries({ queryKey: ["redemptionRequestsListing"] });
        setPayoutTarget(null);
      })
      .catch((e) => {
        if (e.response?.status === 403) {
          showSnackbar("You do not have permission to perform this operation.");
        } else {
          showSnackbar(e?.response?.data?.error?.message || "Something went wrong");
        }
      })
      .finally(() => setPayoutSubmitting(false));
  }

  return (
    <>
      <CardContent sx={{ p: { xs: 2.5, sm: 3 } }}>
        <Box
          display="flex"
          alignItems="center"
          justifyContent="flex-end"
          flexWrap="wrap"
          gap={1.5}
          mb={2}
        >
          <OrgSelector lockToOwnOrg />
          <ToolbarButton
            icon={FilterIcon}
            label="Filters"
            badgeCount={activeFilterCount}
            onClick={() => setFilterDialogOpen(true)}
          />
        </Box>

        {isLoading ? (
          <Box display="flex" justifyContent="center" py={8}>
            <Loading />
          </Box>
        ) : isError ? (
          <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" py={8} gap={0.5}>
            <Typography fontWeight={600} color={black}>
              Something went wrong
            </Typography>
            <Typography variant="body2" color={greyEnabled}>
              We couldn't load the redemption requests. Please try again.
            </Typography>
          </Box>
        ) : showEmpty ? (
          <TableEmptyState
            message="No redemption requests found"
            hint="Try adjusting your filters."
          />
        ) : (
          <Box mt={1}>
            <TableHeaderRow
              labels={[
                { label: "Sl. No.", flex: 0.3 },
                { label: "User", flex: 1.6 },
                { label: "Points Requested", flex: 0.7 },
                { label: "Status", flex: 1 },
                { label: "Payout Status", flex: 1 },
                { label: "Requested On", flex: 1 },
                { label: "Actions" },
              ]}
              lastColumnWidth={140}
              align="left"
              bordered
            />
            {list.map((item, index) => {
              const statusColors = statusChipColors(item.status);
              return (
                <TableRow key={item.id} bordered>
                  <TableCell label="Sl. No." flex={0.3} bordered>
                    <Typography fontSize={14} color={black} noWrap>
                      {(page - 1) * pageSize + index + 1}
                    </Typography>
                  </TableCell>
                  <TableCell label="User" flex={1.6} bordered>
                    <Typography fontWeight={600} color={black} noWrap>
                      {item.userName || "-"}
                    </Typography>
                    <Typography variant="body2" color={greyEnabled} noWrap>
                      {item.mobileNo || "-"}
                    </Typography>
                  </TableCell>
                  <TableCell label="Points Requested" flex={0.7} bordered>
                    <Typography fontSize={14} fontWeight={600} color={purple} noWrap>
                      {item.points}
                    </Typography>
                  </TableCell>
                  <TableCell label="Status" flex={1} bordered>
                    <Chip
                      label={item.status}
                      size="small"
                      sx={{ bgcolor: statusColors.bg, color: statusColors.color, fontWeight: 600 }}
                    />
                  </TableCell>
                  <TableCell label="Payout Status" flex={1} bordered>
                    {item.payoutStatus ? (
                      <Chip
                        label={item.payoutStatus}
                        size="small"
                        sx={{
                          bgcolor: statusChipColors(item.payoutStatus).bg,
                          color: statusChipColors(item.payoutStatus).color,
                          fontWeight: 600,
                        }}
                      />
                    ) : (
                      <Typography fontSize={14} color={greyEnabled}>
                        -
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell label="Requested On" flex={1} bordered>
                    <Typography fontSize={14} color={black} noWrap>
                      {item.createdAt ? dayjs(item.createdAt).format("DD MMM YYYY") : "-"}
                    </Typography>
                  </TableCell>
                  <TableCell isLast lastColumnWidth={140} bordered>
                    <Box display="flex" justifyContent={{ xs: "flex-start", sm: "flex-end" }} gap={0.5}>
                      <Tooltip title="View details">
                        <IconButton
                          onClick={() =>
                            navigate(`/rewards/redemptionRequestDetails/${item.id}`, {
                              state: { redemptionRequest: item },
                            })
                          }
                          sx={{ bgcolor: greenLight, "&:hover": { bgcolor: "#D6EEE0" } }}
                        >
                          <VisibilityRounded sx={{ color: green, fontSize: 20 }} />
                        </IconButton>
                      </Tooltip>
                      {item.status === "In Progress" && hasPermission("rewards", "update") && (
                        <Tooltip title="Update payout status">
                          <IconButton
                            onClick={() => setPayoutTarget(item)}
                            sx={{ bgcolor: "#EDE7F6", "&:hover": { bgcolor: "#E0D4F2" } }}
                          >
                            <PaymentsOutlined sx={{ color: purple, fontSize: 20 }} />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              );
            })}
          </Box>
        )}
      </CardContent>

      {showList && (
        <CardActions sx={{ px: 4, pb: 3 }}>
          <Box width="100%">
            <TableFooterPagination
              count={count}
              page={page - 1}
              onPageChange={handleChangePage}
              summaryText={customText}
              rowsPerPage={pageSize}
              rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
              onRowsPerPageChange={setPageSize}
            />
          </Box>
        </CardActions>
      )}

      {filterDialogOpen && (
        <FilterRedemptionRequests
          open={filterDialogOpen}
          onClose={() => setFilterDialogOpen(false)}
          status={filters.status}
          payoutStatus={filters.payoutStatus}
          onApply={setFilters}
        />
      )}

      <UpdatePayoutDialog
        open={!!payoutTarget}
        onClose={closePayout}
        onConfirm={confirmPayout}
        loading={payoutSubmitting}
      />
    </>
  );
}
